import { useEffect, useRef } from 'react';

/**
 * useUnsavedWarning
 *
 * Uses the "history sentinel" technique to reliably block browser back button.
 * When dirty, we push an extra history entry — so back button hits that first.
 * On popstate, we push another sentinel to keep trapping the user.
 *
 * @param {boolean} isDirty
 */
const useUnsavedWarning = (isDirty) => {
  const isDirtyRef   = useRef(isDirty);
  const toastRef     = useRef(null);
  const reshowTimer  = useRef(null);
  const autoDismiss  = useRef(null); // auto-hide after N seconds
  const origPushRef  = useRef(null);

  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);

  // ── Styles ─────────────────────────────────────────────────────────────────
  const ensureStyles = () => {
    if (document.getElementById('__uw-styles')) return;
    const s = document.createElement('style');
    s.id = '__uw-styles';
    s.textContent = `
      @keyframes uw-in    { from{opacity:0;transform:translateY(-16px) scale(.96)} to{opacity:1;transform:translateY(0) scale(1)} }
      @keyframes uw-out   { from{opacity:1;transform:translateY(0) scale(1)}       to{opacity:0;transform:translateY(-16px) scale(.96)} }
      @keyframes uw-blink { 0%,100%{opacity:1} 50%{opacity:.3} }
      @keyframes uw-shake { 0%,100%{transform:translateX(0)} 20%{transform:translateX(-7px)} 40%{transform:translateX(7px)} 60%{transform:translateX(-4px)} 80%{transform:translateX(4px)} }

      #__uw-toast {
        position:fixed; top:24px; right:24px; z-index:99999;
        animation:uw-in .28s cubic-bezier(.21,1.02,.73,1) forwards;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI','Inter',Roboto,sans-serif;
      }
      #__uw-toast.uw-hide  { animation:uw-out .22s ease-in forwards; }
      #__uw-toast.uw-shake { animation:uw-shake .42s ease; }

      #__uw-toast .uw-box {
        display:flex; align-items:flex-start; gap:12px;
        background:#fff; border:1px solid #e5e7eb; border-left:4px solid #ef4444;
        border-radius:10px; padding:14px 16px;
        min-width:280px; max-width:340px;
        box-shadow:0 4px 6px -1px rgba(0,0,0,.07),0 10px 24px -4px rgba(0,0,0,.1);
      }
      #__uw-toast .uw-icon-wrap {
        margin-top:1px; width:20px; height:20px; flex-shrink:0;
        border-radius:50%; background:#fee2e2; border:1.5px solid #ef4444;
        display:flex; align-items:center; justify-content:center;
      }
      #__uw-toast .uw-body { flex:1; min-width:0; }
      #__uw-toast .uw-title {
        font-size:13.5px; font-weight:600; color:#111827;
        line-height:1.35; display:flex; align-items:center; gap:6px;
      }
      #__uw-toast .uw-dot {
        width:6px; height:6px; border-radius:50%; background:#ef4444;
        display:inline-block; animation:uw-blink 1.8s ease-in-out infinite; flex-shrink:0;
      }
      #__uw-toast .uw-sub { font-size:12px; color:#6b7280; margin-top:3px; line-height:1.45; }
      #__uw-toast .uw-close {
        background:none; border:none; cursor:pointer; padding:2px; color:#9ca3af;
        display:flex; align-items:center; justify-content:center;
        border-radius:4px; flex-shrink:0; transition:background .15s,color .15s; margin-top:-1px;
      }
      #__uw-toast .uw-close:hover { background:#f3f4f6; color:#374151; }
    `;
    document.head.appendChild(s);
  };

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = () => {
    if (toastRef.current) return;
    ensureStyles();
    const el = document.createElement('div');
    el.id = '__uw-toast';
    el.innerHTML = `
      <div class="uw-box">
        <div class="uw-icon-wrap">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M5 1L9 9H1L5 1Z" stroke="#ef4444" stroke-width="1.5" stroke-linejoin="round" fill="none"/>
            <rect x="4.5" y="4" width="1" height="2.5" rx=".5" fill="#ef4444"/>
            <rect x="4.5" y="7.2" width="1" height="1" rx=".5" fill="#ef4444"/>
          </svg>
        </div>
        <div class="uw-body">
          <div class="uw-title"><span class="uw-dot"></span>Unsaved Changes</div>
          <div class="uw-sub">Save your changes before leaving this page.</div>
        </div>
        <button class="uw-close" id="__uw-close" title="Dismiss">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
          </svg>
        </button>
      </div>`;
    document.body.appendChild(el);
    toastRef.current = el;

    // X button — hide immediately
    document.getElementById('__uw-close').addEventListener('click', () => {
      hideToast();
    });
  };

  const hideToast = () => {
    clearTimeout(autoDismiss.current);
    const el = toastRef.current;
    if (!el) return;
    el.classList.add('uw-hide');
    setTimeout(() => { el.remove(); toastRef.current = null; }, 240);
  };


  const shakeToast = () => {
    if (!toastRef.current) { showToast(); return; } // re-show if dismissed
    toastRef.current.classList.remove('uw-shake');
    void toastRef.current.offsetWidth;
    toastRef.current.classList.add('uw-shake');
    setTimeout(() => toastRef.current?.classList.remove('uw-shake'), 420);
  };

  // ── Show/hide on isDirty change ────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(reshowTimer.current);
    clearTimeout(autoDismiss.current);
    if (isDirty) {
      // Push sentinel so browser back button hits it first
      if (origPushRef.current) {
        origPushRef.current({ __uw_sentinel: true }, '');
      }
    } else {
      hideToast();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  // ── Block tab close / refresh ──────────────────────────────────────────────
  useEffect(() => {
    const guard = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => window.removeEventListener('beforeunload', guard);
  }, []);

  // ── Intercept SPA nav + browser back (sentinel technique) ─────────────────
  useEffect(() => {
    const origPush    = window.history.pushState.bind(window.history);
    const origReplace = window.history.replaceState.bind(window.history);

    // Store so the isDirty effect can push sentinels
    origPushRef.current = origPush;

    // Patch pushState — block when dirty
    window.history.pushState = function (...args) {
      if (!isDirtyRef.current) return origPush(...args);
      shakeToast();
    };

    // Patch replaceState — block when dirty
    window.history.replaceState = function (...args) {
      if (!isDirtyRef.current) return origReplace(...args);
      shakeToast();
    };

    /**
     * Browser back / forward — sentinel technique:
     * When dirty and user hits back, popstate fires.
     * We immediately push another sentinel to trap them here.
     * React Router doesn't re-render because the pathname stays the same.
     */
    const onPopState = () => {
      if (!isDirtyRef.current) return;
      // Push sentinel again to keep user trapped
      origPush({ __uw_sentinel: true }, '');
      shakeToast();
    };

    window.addEventListener('popstate', onPopState);

    return () => {
      window.history.pushState    = origPush;
      window.history.replaceState = origReplace;
      window.removeEventListener('popstate', onPopState);
      origPushRef.current = null;
      clearTimeout(reshowTimer.current);
      hideToast();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useUnsavedWarning;
